import { useCallback, useEffect, useState } from 'react';
import { getSubscriptionTypes } from '../../public/common/util/category';
import { SubscriptionCategory } from '../enums';
import {
  fetchBillingAccounts,
  fetchCustomerOrders,
  fetchInvoices,
  fetchSubscriptionActions,
  fetchSubscriptions,
  fetchSupportCases,
} from '../fetch.js';
import type { BillingAccountHeader } from '../../generated/api/billingAccountHeader';
import type { CustomerOrderHeader } from '../../generated/api/customerOrderHeader';
import type { InvoiceHeader } from '../../generated/api/invoiceHeader';
import type { SubscriptionActionHeader } from '../../generated/api/subscriptionActionHeader';
import type { SubscriptionHeader } from '../../generated/api/subscriptionHeader';
import type { SupportCaseDataBundle } from '../../generated/api/supportCaseDataBundle';

const createEmptyRecord = <T>(defaultValue: T): Record<SubscriptionCategory, T> =>
  Object.values(SubscriptionCategory).reduce(
    (acc, key) => ({ ...acc, [key]: defaultValue }),
    {} as Record<SubscriptionCategory, T>
  );

const useFetchedResource = <T>(fetchFn: () => Promise<T[]>): [T[], boolean] => {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchFn()
      .then(res => setData(res))
      .finally(() => setLoading(false));
  }, [fetchFn]);

  return [data, loading];
};

const fetchCategorySubscriptions = async (
  category: SubscriptionCategory,
  setSubscriptions: React.Dispatch<React.SetStateAction<Record<SubscriptionCategory, SubscriptionHeader[]>>>,
  setLoading: React.Dispatch<React.SetStateAction<Record<SubscriptionCategory, boolean>>>
) => {
  setLoading(p => ({ ...p, [category]: true }));
  try {
    const response = await fetchSubscriptions({
      details: true,
      useSearchService: true,
      subscriptionType: getSubscriptionTypes(category),
    });
    const results = response?.searchResults?.map(r => r.result) || [];

    setSubscriptions(p => ({ ...p, [category]: results.flat() }));
  } finally {
    setLoading(p => ({ ...p, [category]: false }));
  }
};

const useMergedSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState(createEmptyRecord<SubscriptionHeader[]>([]));
  const [loading, setLoading] = useState(createEmptyRecord<boolean>(false));

  useEffect(() => {
    const validCategories = Object.values(SubscriptionCategory).filter(c => getSubscriptionTypes(c)?.length);

    for (const category of validCategories) {
      fetchCategorySubscriptions(category, setSubscriptions, setLoading);
    }
  }, []);

  return { subscriptions, loading };
};

export const useSelfServiceData = () => {
  const { subscriptions, loading } = useMergedSubscriptions();

  const fetchBillingAccountsData = useCallback(async () => {
    const res = await fetchBillingAccounts({ useSearchService: true });
    return res?.searchResults?.map(r => r.result) ?? [];
  }, []);
  const fetchAllInvoices = useCallback(async () => {
    const res = await fetchInvoices({ useSearchService: true });
    return res?.searchResults?.map(r => r.result) ?? [];
  }, []);
  const fetchCustomerOrdersData = useCallback(async () => {
    const res = await fetchCustomerOrders({ sort: 'created', order: 'desc', useSearchService: true }, false);
    return res?.searchResults?.map(r => r.result) ?? [];
  }, []);
  const fetchPendingRedeemRequests = useCallback(async () => {
    const res = await fetchSubscriptionActions({ useSearchService: true }, false);
    return res?.searchResults?.map(r => r.result).filter(r => r.status === 'PENDING_APPROVAL') ?? [];
  }, []);
  const fetchSupportCasesData = useCallback(async () => {
    const res = await fetchSupportCases({ sort: 'lastModified', order: 'desc' });
    return res?.searchResults?.map(r => r.result) ?? [];
  }, []);

  const [billingAccounts, _billingAccountsLoading] = useFetchedResource<BillingAccountHeader>(fetchBillingAccountsData);
  const [allInvoices, invoicesLoading] = useFetchedResource<InvoiceHeader>(fetchAllInvoices);
  const [customerOrders, ordersLoading] = useFetchedResource<CustomerOrderHeader>(fetchCustomerOrdersData);
  const [pendingRedeemRequests, redeemRequestsLoading] =
    useFetchedResource<SubscriptionActionHeader>(fetchPendingRedeemRequests);
  const [supportCases, supportCasesLoading] = useFetchedResource<SupportCaseDataBundle>(fetchSupportCasesData);

  const openInvoices = allInvoices.filter(i => i.balance && i.balance > 0);

  return {
    subscriptions,
    loading,
    allInvoices,
    billingAccounts,
    openInvoices,
    customerOrders,
    pendingRedeemRequests,
    supportCases,
    summaryLoading: {
      invoices: invoicesLoading,
      orders: ordersLoading,
      redeemRequests: redeemRequestsLoading,
      supportCases: supportCasesLoading,
    },
  };
};
